import type { TextProps } from "@chakra-ui/layout";
import { t } from "@lingui/macro";
import { captureEvent } from "@sentry/react";
import {
  type AccountMoveFragment,
  AddAccountMovesToDocumentDocument,
  type AddAccountMovesToDocumentMutation,
  type AddAccountMovesToDocumentMutationVariables,
  type BankAccountMoveDocument,
  BankAccountMovesDocument,
  type BankAccountMovesQuery,
  type BankAccountMovesQueryVariables,
  BankAccountWhereColumn,
  BankDocumentTypeEnum,
  type Currency,
  type Scalars,
  SqlOperator,
} from "@src/__generated__/graphql";
import { BankStore } from "@src/components/modules/bank/stores";
import { ACTIONS_COLUMN_ID } from "@src/components/ui-kit";
import { FetchHelper } from "@src/helpers/apollo/fetch";
import { MutationHelper } from "@src/helpers/apollo/mutation";
import type { AppStore } from "@src/stores/AppStore";
import { EXCHANGE_RATE_DENOMINATION } from "@src/stores/forms/SettingsModalState/FinancialSettingsState";
import type { ModalStore } from "@src/stores/ModalStore";
import { Filter, Filters } from "@src/utils/components/filters/models";
import { toApiDate } from "@src/utils/dates";
import { currency, denominate } from "@src/utils/formatters";
import mapToOptions from "@src/utils/map-to-options";
import { BooleanState } from "@src/utils/mobx/states/BooleanState";
import { DisclosureState } from "@src/utils/mobx/states/DisclosureState";
import { PaginationState } from "@src/utils/mobx/states/PaginationState";
import { action, computed, makeObservable, observable } from "mobx";
import { MatchPaymentModal } from ".";
import { SelectedMove } from "./models/selected-move";

type Document = {
  id: Scalars["ID"]["output"];
  number: string;
  amount: number;
  currency: Currency;
  exchangeRate: number;
  type: BankDocumentTypeEnum;
};
interface SelectedMoveArg
  extends Pick<
    BankAccountMoveDocument,
    "amount" | "bank_account_move_id" | "exchange_rate"
  > {
  bankAccountMove?: AccountMoveFragment | null;
}
type MatchPaymentModalDisclosureData = {
  document: Document;
  selectedMoves: SelectedMoveArg[];
  onUpdate?: () => Promise<void>;
};
export class MatchPaymentModalStore implements ModalStore {
  readonly modalId = "match-payment-match-modal";
  readonly tableKey = `${this.modalId}-table`;

  isOnUpdateLoading = new BooleanState(false);

  disclosure = new DisclosureState<MatchPaymentModalDisclosureData>({
    onOpen: (additionalData) => {
      this.where.reset([], true);
      this.searchTerm = "";
      this.pagination.currentPage = this.pagination.initialPage;

      if (additionalData?.selectedMoves) {
        for (const move of additionalData.selectedMoves) {
          this.selectedIds.push(move.bank_account_move_id);
          this.selectedMoves.push(
            new SelectedMove(
              {
                amount: move.amount.toString(),
                moveId: move.bank_account_move_id,
                exchangeRate: move.exchange_rate.toString(),
              },
              move.bankAccountMove?.currency ??
                this.appStore.workspaceStore.settings!.currency,
              additionalData.document?.currency,
            ),
          );
        }
      }

      this.where.filtersByColumn
        .get(BankAccountWhereColumn.BankAccountId)
        ?.setOptions(
          mapToOptions.companiesToBankAccountOptions(
            this.appStore.workspaceStore.companies,
          ),
        );
      this.appStore.UIStore.dialogs.openModal({
        id: this.modalId,
        content: <MatchPaymentModal />,
      });
      this.fetchData();
    },
    onClose: () => {
      this.selectedIds = [];
      this.selectedMoves = [];
      this.tableData = [];
      this.appStore.UIStore.dialogs.closeModal(this.modalId);
    },
  });

  fetcher = new FetchHelper<
    BankAccountMovesQuery,
    BankAccountMovesQueryVariables
  >(BankAccountMovesDocument);

  private mutator = new MutationHelper<
    AddAccountMovesToDocumentMutation,
    AddAccountMovesToDocumentMutationVariables
  >(AddAccountMovesToDocumentDocument);

  @observable.ref searchTerm = "";
  where = new Filters<BankAccountWhereColumn>(
    [
      new Filter({
        title: t`Bank Account`,
        column: BankAccountWhereColumn.BankAccountId,
        operator: SqlOperator.In,
        options: [],
      }),
      new Filter({
        title: t`Status`,
        column: BankAccountWhereColumn.Status,
        operator: SqlOperator.In,
        options: BankStore.getStatusFilterOptions(),
      }),
      new Filter({
        hidden: true,
        title: t`Amount from`,
        column: BankAccountWhereColumn.AmountFrom,
        operator: SqlOperator.Gte,
        options: [],
        mutateToUrl: BankStore.mutateAmountFilter,
      }),
      new Filter({
        hidden: true,
        title: t`Amount to`,
        column: BankAccountWhereColumn.AmountTo,
        operator: SqlOperator.Lte,
        options: [],
        mutateToUrl: BankStore.mutateAmountFilter,
      }),
    ],
    {
      onChange: () => {
        this.fetchData();
      },
    },
  );
  pagination = new PaginationState(`${this.tableKey}-pagination`, {
    onChangePagination: () => this.fetchData(),
  });

  @observable tableData: AccountMoveFragment[] = [];
  @observable.ref selectedIds: string[] = [];
  @observable selectedMoves: SelectedMove[] = [];

  constructor(private appStore: AppStore) {
    makeObservable(this);
  }

  @computed
  get isSubmitting(): boolean {
    return this.mutator.isLoading.value || this.isOnUpdateLoading.value;
  }

  @computed get columns() {
    const cols = BankStore.getColumnsWithoutActions();

    cols.push({
      id: ACTIONS_COLUMN_ID,
      meta: { label: t`Actions` },
      header: () => t`Actions`,
      cell: () => null,
    });

    return cols;
  }

  @computed get selectedDocument(): Document | undefined {
    return this.disclosure.additionalData?.document;
  }

  @computed get moveHasForeignCurrency() {
    return this.selectedMoves.some((move) => move.hasForeignCurrency);
  }

  @computed get selectedMovesIncludeForeignCurrency(): boolean {
    if (!this.selectedMoves.length) return false;
    const referenceCurrency = this.selectedDocument?.currency;
    if (!referenceCurrency) return false;
    return this.selectedMoves.some(
      (move) => move.currency.id !== referenceCurrency.id,
    );
  }

  @computed
  get selectedMovesAmount(): number {
    return this.selectedMoves.reduce(
      (prev, curr) => prev + curr.denominatedAmountInDocumentCurrency,
      0,
    );
  }

  @computed get balance(): number {
    if (!this.selectedDocument) return 0;
    const documentAmount = Math.abs(this.selectedDocument.amount);
    const selectedMovesInDocumentCurrency = this.selectedMoves.reduce(
      (prev, curr) => prev + curr.denominatedAmountInDocumentCurrency,
      0,
    );
    if (this.selectedDocument.type === BankDocumentTypeEnum.Expense) {
      return documentAmount + selectedMovesInDocumentCurrency;
    }
    return documentAmount - selectedMovesInDocumentCurrency;
  }

  @computed get isBalancePositive(): boolean {
    return Math.sign(this.balance) >= 0;
  }

  @computed get balanceColor(): TextProps["color"] {
    if (this.balance === 0) return "green.400";
    return this.balance < 0 ? "red.500" : undefined;
  }

  @computed
  get formattedBalance(): string {
    if (!this.selectedDocument)
      return currency.formatByWorkspace(Math.abs(this.balance));
    return currency.formatByCurrency(
      Math.abs(this.balance),
      this.selectedDocument.currency,
    );
  }

  @action.bound handleSearch(value: string) {
    this.searchTerm = value;
    this.fetchData();
  }

  @action.bound async fetchData() {
    const [data, error] = await this.fetcher.fetch(
      {
        filters: {
          where: this.where.asWhereParam,
          search: this.searchTerm,
        },
        page: this.pagination.asParams.page,
        first: this.pagination.asParams.first,
      },
      undefined,
    );

    if (error || !data.bankAccountMoves) return;

    this.tableData = data.bankAccountMoves.data;
    this.pagination.setFromPaginatorInfo(data.bankAccountMoves.paginatorInfo);
  }

  @action.bound handleSelect(ids: string[]) {
    this.selectedIds = ids;
    this.removeMoveFromSelected(ids);
    this.addMoveToSelected(ids);
  }

  @action.bound removeMoveFromSelected(ids: string[]) {
    const idsToRemove: string[] = [];
    const documentCount = this.selectedMoves.length;

    for (let i = 0; i < documentCount; i++) {
      const move = this.selectedMoves[i];
      if (ids.includes(move.$.moveId.value)) continue;
      idsToRemove.push(move.$.moveId.value);
    }

    for (const id of idsToRemove) {
      const index = this.selectedMoves.findIndex(
        (move) => move.$.moveId.value === id,
      );
      if (index === -1) continue;

      this.selectedMoves.splice(index, 1);
    }
  }

  @action.bound addMoveToSelected(ids: string[]) {
    for (const id of ids) {
      const item = this.tableData.find((item) => item.id === id);
      if (!item) continue;

      const existingMove = this.selectedMoves.find(
        (move) => move.$.moveId.value === item.id,
      );
      if (existingMove) continue;
      const passedMove = this.disclosure.additionalData?.selectedMoves.find(
        (move) => move.bank_account_move_id === item.id,
      );

      this.selectedMoves.push(
        new SelectedMove(
          {
            moveId: item.id,
            amount: passedMove
              ? passedMove.amount.toString()
              : item.balance.toString(),
            exchangeRate:
              passedMove?.exchange_rate?.toString() ??
              denominate(1, EXCHANGE_RATE_DENOMINATION).toString(),
          },
          item.currency,
          this.selectedDocument?.currency,
        ),
      );
    }
  }

  @action.bound handleMoveDelete(move: SelectedMove) {
    this.handleSelect(
      this.selectedIds.filter((id) => id !== move.$.moveId.value),
    );
  }

  @action.bound async handleConfirm() {
    if (!this.selectedDocument) {
      this.appStore.UIStore.toast({
        status: "error",
        title: t`Something went wrong, please try again later.`,
      });
      captureEvent({
        message:
          "FE: Missing selected document from MatchPaymentModal handleConfirm function.",
      });
      return;
    }
    const [_, error] = await this.mutator.mutate({
      documentId: this.selectedDocument.id,
      date: toApiDate(new Date()),
      documentType: this.selectedDocument.type,
      bankAccountMoves: this.selectedMoves.map((move) => move.mutationInput),
    });

    if (error) return;
    this.isOnUpdateLoading.on();
    await this.disclosure.additionalData?.onUpdate?.();
    this.isOnUpdateLoading.off();
    this.disclosure.onClose();
  }

  @computed
  get amountFromInputValue(): string {
    return (
      this.where.filtersByColumn.get(BankAccountWhereColumn.AmountFrom)
        ?.value?.[0] ?? ""
    );
  }

  @computed
  get amountToInputValue(): string {
    return (
      this.where.filtersByColumn.get(BankAccountWhereColumn.AmountTo)
        ?.value?.[0] ?? ""
    );
  }

  @action.bound
  handleChangeAmountFrom(value: string) {
    this.where.filtersByColumn
      .get(BankAccountWhereColumn.AmountFrom)
      ?.setValue(value);
  }

  @action.bound
  handleChangeAmountTo(value: string) {
    this.where.filtersByColumn
      .get(BankAccountWhereColumn.AmountTo)
      ?.setValue(value);
  }

  @computed get isConfirmDisabled(): boolean {
    return (
      !this.isBalancePositive ||
      this.selectedMoves.some((move) => move.isEditEnabled.value)
    );
  }
}
